import React from "react";
import { useVsiAppContext } from "../stores/VsiApp";
import { withErrorBoundary } from "../boundaries/withErrorBoundary";
import GeneralErrorBoundary from "../boundaries/GeneralErrorBoundary";
import {
  Button,
  Col,
  Form,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row
} from "reactstrap";
import VsiLabeledSelect from "./VsiLabeledSelect";
import { DeleteAuth, PostAuth, PutAuth } from "../utils/Verbs";

const decodingMethodOptions = [
  { value: "greedy", label: "Greedy" },
  { value: "sample", label: "Sampling" }
];

function AutobotVmwareForm({
                             isAdjustModalOpen,
                             toggleAdjustModal,
                             adjustments,
                             setAdjustments,
                             defaultValues,
                             prompt,
                             setPrompt,
                             defaultPrompt,
                             savedAdjustments
                           }) {
  const { theme, user, getAccessTokenSilently } = useVsiAppContext();

  const onInputChange = (e) => {
    const { name, value } = e.target;

    let typeCorrectedValue;
    if (typeof (adjustments[name]) === "number") {
      typeCorrectedValue = JSON.parse(value);
    } else {
      typeCorrectedValue = value;
    }

    setAdjustments({
      ...adjustments,
      [name]: typeCorrectedValue
    });
  };

  const onPromptChange = (e) => {
    setPrompt(e.target.value);
  };

  const saveAdjustments = async () => {
    const token = await getAccessTokenSilently();
    if (savedAdjustments._id) {
      // Update existing
      try {
        await PutAuth(`/api/v6/admin/autobot_prompt/${savedAdjustments._id}`, { ...adjustments, prompt }, token);
      } catch (error) {
        console.log("Error while updating");
      }
      ;
    } else {
      // Create new
      try {
        await PostAuth(`/api/v6/admin/autobot_prompt`, { ...adjustments, prompt, user: user._id }, token);
      } catch (error) {
        console.log("Error while saving");
      }
      ;
    }
    toggleAdjustModal();
  };

  const resetAdjustments = async () => {
    // DELETE saved adjustments and reset to defaults
    try {
      const token = await getAccessTokenSilently();
      await DeleteAuth(`/api/v6/admin/autobot_prompt/${savedAdjustments._id}`, token);
      // Reset to default
      setAdjustments(defaultValues);
      setPrompt(defaultPrompt);
    } catch (error) {
      console.log("Deletion error");
    }
    toggleAdjustModal();
  };

  const cancelAdjustmentChanges = () => {
    // Change back to pre-change values
    let { prompt, ...otherAdjustments } = savedAdjustments;
    setAdjustments(otherAdjustments);
    setPrompt(prompt);
    toggleAdjustModal();
  };

  return (
    <Modal isOpen={isAdjustModalOpen} toggle={toggleAdjustModal} size={"xl"} className={`bg-${theme}`}>
      <ModalHeader toggle={toggleAdjustModal}
                   className={`bg-${theme} ${theme === "dark" ? "text-light" : "text-dark"}`}>
        Adjust Artificial Intelligence (AI) Output
      </ModalHeader>
      <ModalBody className={`bg-${theme} ${theme === "dark" ? "text-light" : "text-dark"}`}>
        <Form>
          <Row>
            <Col md={6}>
              <Row>
                <Label for="decodingMethod">
                  Decoding Method
                </Label>
              </Row>
              <Row>
                <FormGroup>
                  <VsiLabeledSelect
                    maxWidth="100%"
                    id="decodingMethod"
                    name="decodingMethod"
                    type="select"
                    options={decodingMethodOptions}
                    defaultValue={decodingMethodOptions.filter(method => method.value === adjustments.decodingMethod)}
                    onChange={e => {
                      setAdjustments({
                        ...adjustments,
                        decodingMethod: e.value
                      });
                    }}
                  />
                </FormGroup>
              </Row>
              <Row>
                <Col>
                  <FormGroup>
                    <Label for="minNewTokens">
                      Minimum New Tokens
                    </Label>
                    <Input
                      id="minNewTokens"
                      name="minNewTokens"
                      placeholder="Upload Folder"
                      defaultValue={adjustments.minNewTokens}
                      type="number"
                      onChange={e => {
                        onInputChange(e);
                      }}
                      min={0}
                    />
                  </FormGroup>
                </Col>
                <Col>
                  <FormGroup>
                    <Label for="maxNewTokens">
                      Maximum New Tokens
                    </Label>
                    <Input
                      id="maxNewTokens"
                      name="maxNewTokens"
                      defaultValue={adjustments.maxNewTokens}
                      onChange={e => {
                        onInputChange(e);
                      }}
                      type="number"
                      min={0}
                    />
                  </FormGroup>
                </Col>
              </Row>
              <Col>
                <FormGroup>
                  <Label for="randomSeed">
                    Random Seed
                  </Label>
                  <Input
                    id="randomSeed"
                    name="randomSeed"
                    defaultValue={adjustments.randomSeed}
                    placeholder="None"
                    onChange={e => {
                      onInputChange(e);
                    }}
                    type="number"
                  />
                </FormGroup>
              </Col>
              <Col>
                <FormGroup>
                  <Label for="stopSequences">
                    Stop Sequences <small>(separate each sequence with a comma)</small>
                  </Label>
                  <Input
                    id="stopSequences"
                    name="stopSequences"
                    placeholder="None"
                    defaultValue={adjustments.stopSequences}
                    onChange={e => {
                      onInputChange(e);
                    }}
                    type="text"
                  />
                </FormGroup>
              </Col>
              <Col>
                <FormGroup>
                  <Label for="temperature">
                    Temperature
                  </Label>
                  <Input
                    id="temperature"
                    name="temperature"
                    defaultValue={adjustments.temperature}
                    onChange={e => {
                      onInputChange(e);
                    }}
                    type="number"
                    min={0}
                    max={2}
                  />
                </FormGroup>
              </Col>
              <Row>
                <Col>
                  <FormGroup>
                    <Label for="topK">
                      Top K
                    </Label>
                    <Input
                      id="topK"
                      name="topK"
                      defaultValue={adjustments.topK}
                      onChange={e => {
                        onInputChange(e);
                      }}
                      type="number"
                      min={1}
                      max={100}
                    />
                  </FormGroup>
                </Col>
                <Col>
                  <FormGroup>
                    <Label for="topP">
                      Top P (Nucleus Sampling)
                    </Label>
                    <Input
                      id="topP"
                      name="topP"
                      defaultValue={adjustments.topP}
                      onChange={e => {
                        onInputChange(e);
                      }}
                      type="number"
                      min={0}
                      max={1}
                    />
                  </FormGroup>
                </Col>
              </Row>
              <Col>
                <FormGroup>
                  <Label for="repetitionPenalty">
                    Repetition Penalty
                  </Label>
                  <Input
                    id="repetitionPenalty"
                    name="repetitionPenalty"
                    defaultValue={adjustments.repetitionPenalty}
                    onChange={e => {
                      onInputChange(e);
                    }}
                    type="number"
                    min={1}
                    max={2}
                  />
                </FormGroup>
              </Col>
            </Col>
            <Col md={6}>
              {/* I know the .3 looks silly, but it makes it line up perfectly with the input box next to it */}
              <FormGroup style={{ height: "92.3%" }}>
                <Label for="prompt">
                  Prompt
                </Label>
                <Input
                  id="prompt"
                  name="prompt"
                  value={prompt}
                  type="textarea"
                  onChange={e => {
                    onPromptChange(e);
                  }}
                  style={{ width: "100%", height: "100%" }}
                />
              </FormGroup>
            </Col>
          </Row>
        </Form>
      </ModalBody>
      <ModalFooter className={`bg-${theme} ${theme === "dark" ? "text-light" : "text-dark"}`}>
        <Button color={"primary"} onClick={saveAdjustments}>
          Update
        </Button>
        <Button color={"danger"} onClick={resetAdjustments}>
          Reset
        </Button>
        <Button onClick={cancelAdjustmentChanges}>
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default withErrorBoundary(AutobotVmwareForm, GeneralErrorBoundary);
